// Shared cadence helpers: labels, rule-editor primitives and the email preview
// sandbox (also used by the template editor). The step graph lives in graph.utils.js.

export const EMPTY_RULE = { op: 'and', conditions: [] }

// "1 workflow" / "2 workflows". Every count shown to an admin goes through this
// so no screen reads "1 paths".
export const pluralize = (count, singular, plural = `${singular}s`) => {
	const value = Number(count) || 0
	return `${value.toLocaleString('en-US')} ${value === 1 ? singular : plural}`
}

const sentenceCase = (value) => {
	const text = String(value || '')
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
	return text ? text.charAt(0).toUpperCase() + text.slice(1).toLowerCase() : ''
}

export const CADENCE_STATUS_LABELS = { active: 'Live', paused: 'Paused', draft: 'Draft', archived: 'Archived' }

export const cadenceStatusLabel = (status) => CADENCE_STATUS_LABELS[status || 'draft'] || sentenceCase(status)

export const cadenceStatusPillStyle = (status) =>
	({
		active: { color: '#309102', background: '#ECFDF3' },
		paused: { color: '#B26A00', background: '#FFF6DE' },
	}[status] || { color: '#5E5E60', background: '#F5F5F5' })

// What each status does to candidates already enrolled — must match the engine:
// only Archive ends enrolments; a draft has never enrolled anyone.
export const CADENCE_STATUS_CONSEQUENCES = {
	active: 'Sending. New candidates enrol and enrolled candidates continue their cadence.',
	paused: 'No sends. Enrolled candidates hold their place and resume where they stopped.',
	draft: 'Not running. Nobody is enrolled until the workflow is published.',
}

export const cadenceCategoryLabel = (category) =>
	({ profile_completion: 'Profile completion', onboarding: 'Onboarding', re_engagement: 'Re-engagement' }[category] || sentenceCase(category) || 'Condition')

// The list sub-line is the admin-written description; the stable key is only a
// fallback for workflows saved before a description existed.
export const cadenceListSubtitle = (cadence) => String(cadence?.description || '').trim() || cadence?.key || ''

export const cadenceActionBarSummary = ({ enrolledCount, emailCount, pathCount }) =>
	[
		Number(enrolledCount) > 0 ? `${pluralize(enrolledCount, 'candidate')} enrolled` : 'No candidates enrolled yet',
		pluralize(emailCount, 'email'),
		pluralize(pathCount, 'path'),
	].join(' · ')

export const cadenceSplitTitle = (category) => `Split by ${cadenceCategoryLabel(category).toLowerCase()}`

export const ruleConditionCount = (rule) => {
	if (!rule || typeof rule !== 'object') return 0
	const children = rule.conditions || rule.rules
	if (!Array.isArray(children)) return 0
	return children.reduce((count, child) => count + (Array.isArray(child?.conditions || child?.rules) ? ruleConditionCount(child) : 1), 0)
}

export const pathTabSummary = (path) => String(path?.description || '').trim() || `${pluralize(ruleConditionCount(path?.rule), 'condition')} · first match wins`

const RELATIVE_TIME_UNITS = [
	['year', 365 * 24 * 60 * 60],
	['month', 30 * 24 * 60 * 60],
	['day', 24 * 60 * 60],
	['hour', 60 * 60],
	['minute', 60],
]

// "just now" / "3 days ago". Null when the timestamp is missing or unparseable.
export const formatRelativeTime = (value, now = Date.now()) => {
	if (!value) return null
	const time = new Date(value).getTime()
	if (Number.isNaN(time)) return null
	const elapsedSeconds = Math.max(0, Math.floor((now - time) / 1000))
	if (elapsedSeconds < 60) return 'just now'
	const [unit, seconds] = RELATIVE_TIME_UNITS.find(([, unitSeconds]) => elapsedSeconds >= unitSeconds)
	const amount = Math.floor(elapsedSeconds / seconds)
	return `${amount} ${unit}${amount === 1 ? '' : 's'} ago`
}

// The list's "Last edited" column reads the same way as template usage ("3 days ago").
export const formatCadenceUpdatedAt = (value, now = Date.now()) => {
	if (!value) return 'Not yet saved'
	return formatRelativeTime(value, now) || 'Not available'
}

// The backend rule language permits nested all/any groups. A flat row editor
// cannot faithfully round-trip those groups, so it must not pretend it can.
export const hasNestedRuleGroups = (rule) => {
	if (!rule || typeof rule !== 'object') return false
	const children = rule.conditions || rule.rules
	return Array.isArray(children) && children.some((child) => child && (Array.isArray(child.conditions || child.rules) || hasNestedRuleGroups(child)))
}

export const flatRuleValidationMessage = (rule, label) => {
	if (!rule || hasNestedRuleGroups(rule)) return null
	return (rule.conditions || []).reduce((message, condition, index) => {
		if (message) return message
		const prefix = `${label} condition ${index + 1}`
		if (!String(condition?.attribute || '').trim()) return `Choose an attribute for ${prefix}.`
		if (!String(condition?.operator || '').trim()) return `Choose an operator for ${prefix}.`
		if (!['is_set', 'is_not_set'].includes(condition.operator) && (condition.value === undefined || condition.value === null || condition.value === ''))
			return `Enter a value for ${prefix}.`
		if (condition.operator === 'between' && (!Array.isArray(condition.value) || condition.value.length !== 2)) return `Enter exactly two values for ${prefix}.`
		if (['in', 'not_in'].includes(condition.operator) && (!Array.isArray(condition.value) || condition.value.length === 0))
			return `Enter at least one value for ${prefix}.`
		return null
	}, null)
}

// The preview is intentionally not a Liquid runtime. Substitute a small,
// obvious sample set and label everything else so relative/malformed links are
// never accidentally presented as a functioning preview.
export const renderPreviewTemplate = (html) =>
	String(html || '').replace(/{{\s*([^}]+?)\s*}}/g, (_, token) => {
		const key = String(token).trim().toLowerCase()
		if (['first_name', 'first name', 'candidate.first_name'].includes(key)) return 'Alex'
		if (['profile_url', 'candidate.profile_url'].includes(key)) return 'https://example.com/profile'
		return `[merge tag: ${String(token).trim()}]`
	})

// A paragraph of the email that holds nothing but one unstyled link with text only: the call-to-action shape the editor
// shows as a Button (cadence-template-blocks parseButton: one link, its text is the paragraph's whole text, no image).
// CSS cannot see the text around a link (:only-child ignores text nodes), so the preview marks it here, reading the HTML
// with an inert DOMParser (nothing runs or loads), and styles only a[data-preview-cta] as a button. A link inside a
// sentence, one the author styled, or one straight in the email outside a paragraph renders as written, as in the real
// email. The markup is returned untouched when nothing is marked.
const squashText = (value) =>
	String(value || '')
		.replace(/\s+/g, ' ')
		.trim()
const isCallToAction = (paragraph) => {
	const links = paragraph.querySelectorAll('a')
	if (links.length !== 1 || paragraph.querySelector('img')) return false
	const link = links[0]
	if (link.hasAttribute('style') || Array.from(link.childNodes).some((node) => node.nodeType !== 3)) return false
	const text = squashText(link.textContent)
	return Boolean(text) && squashText(paragraph.textContent) === text
}
const markCallToActions = (html) => {
	const doc = new DOMParser().parseFromString(`<!doctype html><body>${html}`, 'text/html')
	const paragraphs = Array.from(doc.body.children).filter((element) => element.localName === 'p' && isCallToAction(element))
	paragraphs.forEach((paragraph) => paragraph.querySelector('a').setAttribute('data-preview-cta', ''))
	return paragraphs.length ? doc.body.innerHTML : html
}

export const safePreviewDocument = (html) => {
	const inertHtml = renderPreviewTemplate(html)
		.replace(/<!doctype\b[^>]*>/gi, '')
		.replace(/<\/?(?:html|head|body)\b[^>]*>/gi, '')
		.replace(/<base\b[^>]*>/gi, '')
		.replace(/<meta\b[^>]*http-equiv\s*=\s*["']?refresh[^>]*>/gi, '')
		.replace(/<\/?(?:script|iframe|object|embed|link)\b[^>]*>/gi, '')
		.replace(/<\/?form\b[^>]*>/gi, '')
		// A browser starts an attribute after a space, a "/" or a closing quote (<a/href=…>, <a href="x"onclick=…>).
		.replace(
			/([\s/"'])(?:href|xlink:href|action|formaction|src|srcset|poster|background|data)\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi,
			'$1 data-preview-resource="blocked"'
		)
		.replace(/([\s/"'])on[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '$1')
		.replace(/url\s*\([^)]*\)/gi, 'none')
		.replace(/@import\s+(?:url\s*\([^)]*\)|["'][^"']*["'])\s*;?/gi, '')
	const markedHtml = markCallToActions(inertHtml)
	return `<!doctype html>
<html>
	<head>
		<meta charset="utf-8">
		<meta name="viewport" content="width=device-width, initial-scale=1">
		<meta http-equiv="Content-Security-Policy" content="default-src 'none'; style-src 'unsafe-inline'; img-src data:">
		<style>
			:root { color-scheme: light; }
			* { box-sizing: border-box; }
			html, body { margin: 0; padding: 0; background: #ffffff; }
			body {
				color: #333335;
				font-family: Arial, Helvetica, sans-serif;
				font-size: 14px;
				line-height: 1.55;
				-webkit-font-smoothing: antialiased;
			}
			.email-preview-content { padding: 24px 20px 32px; overflow-wrap: anywhere; text-align: left; }
			p { margin: 0 0 16px; }
			p:last-child { margin-bottom: 0; }
			h1, h2, h3, h4, h5, h6 { color: #333335; line-height: 1.3; margin: 0 0 16px; }
			a { color: #2f2e99; font-weight: 700; }
			.email-preview-content a[data-preview-cta] {
				display: inline-block;
				padding: 12px 22px;
				border-radius: 4px;
				background: #e8751a;
				color: #ffffff;
				font-size: 14px;
				line-height: 20px;
				text-decoration: none;
			}
			strong, b { font-weight: 700; }
			img, table { max-width: 100%; }
			table { border-collapse: collapse; }
			ul, ol { margin: 0 0 16px; padding-left: 22px; }
			blockquote { margin: 0 0 16px; padding-left: 14px; border-left: 3px solid #e6e6e6; color: #666668; }
		</style>
	</head>
	<body><main class="email-preview-content">${markedHtml}</main></body>
</html>`
}

// Automated messages (amendment A19): the API renders the tenant's real email, the whole document (layout, logo, brand
// stripe, web font, footer), and the preview shows it as it is. Nothing of the frame above is added. The document is
// read with an inert DOMParser (nothing runs or loads) and made safe:
//   - scripts, frames, objects, forms, <base>, http-equiv metas and SVG animation are removed;
//   - every on* attribute goes, and every link keeps its look (an address) but points nowhere ("#");
//   - images load from https (the tenant's hosted logo and assets; the asset host is the API's, which differs per
//     environment, so the rule is "https", not one host) or inline data: images; other sources are removed;
//   - the layout's Google Fonts stylesheet stays, so the email shows in its font; any other stylesheet or @import goes.
// The Content-Security-Policy, first in the head, enforces the same: no script, no connection, images from https/data:,
// styles inline or from Google Fonts. With the iframe's sandbox (no allow-scripts) a script could not run anyway.
// An older API sends the body alone (no <html>/<body>): it keeps the Message templates frame (safePreviewDocument).
export const isFullEmailDocument = (html) => /<(?:html|body)[\s>]/i.test(String(html || ''))

const EMAIL_PREVIEW_POLICY =
	"default-src 'none'; style-src 'unsafe-inline' https://fonts.googleapis.com; font-src https://fonts.gstatic.com; img-src https: data:"
const REMOVED_EMAIL_ELEMENTS = 'script, noscript, template, iframe, frame, frameset, object, embed, applet, portal, form, base, meta[http-equiv], animate, set'
const URL_ATTRIBUTES = new Set([
	'href',
	'xlink:href',
	'action',
	'formaction',
	'src',
	'srcset',
	'poster',
	'background',
	'data',
	'lowsrc',
	'dynsrc',
	'ping',
	'cite',
	'longdesc',
	'usemap',
	'codebase',
	'archive',
	'manifest',
])
const SAFE_IMAGE_SOURCE = /^\s*(?:https:\/\/|data:image\/(?:png|gif|jpe?g|webp);base64,)/i
const FONT_STYLESHEET = /^https:\/\/fonts\.googleapis\.com\//i
// url(…) other than https or an inline image becomes none; an @import other than Google Fonts goes.
const safeCss = (css) =>
	String(css || '')
		.replace(/url\s*\(\s*(?!['"]?\s*(?:https:\/\/|data:image\/))[^)]*\)/gi, 'none')
		.replace(/@import\s+(?:url\s*\(\s*)?(?!['"]?https:\/\/fonts\.googleapis\.com\/)[^;]*;?/gi, '')
		.replace(/expression\s*\(/gi, 'none(')

const safeEmailElement = (element) => {
	const tag = element.localName
	Array.from(element.attributes).forEach(({ name, value }) => {
		const attribute = name.toLowerCase()
		if (attribute.startsWith('on')) element.removeAttribute(name)
		else if (attribute === 'style') element.setAttribute(name, safeCss(value))
		else if (URL_ATTRIBUTES.has(attribute)) {
			if (tag === 'a' || tag === 'area') {
				if (attribute === 'href') element.setAttribute(name, '#')
				else element.removeAttribute(name)
			} else if (tag === 'link' && attribute === 'href') {
				// kept by the stylesheet filter below
			} else if (!(['src', 'background'].includes(attribute) && SAFE_IMAGE_SOURCE.test(value))) {
				element.removeAttribute(name)
			}
		}
	})
}

export const safeEmailPreviewDocument = (html) => {
	if (!isFullEmailDocument(html)) return safePreviewDocument(html)
	const doc = new DOMParser().parseFromString(String(html), 'text/html')
	doc.querySelectorAll(REMOVED_EMAIL_ELEMENTS).forEach((element) => element.remove())
	doc.querySelectorAll('link').forEach((link) => {
		const stylesheet = /(?:^|\s)stylesheet(?:\s|$)/i.test(link.getAttribute('rel') || '')
		if (!stylesheet || !FONT_STYLESHEET.test(link.getAttribute('href') || '')) link.remove()
	})
	doc.querySelectorAll('style').forEach((style) => {
		style.textContent = safeCss(style.textContent)
	})
	doc.querySelectorAll('*').forEach(safeEmailElement)
	doc.querySelectorAll('meta[charset], meta[name="referrer"]').forEach((meta) => meta.remove())
	const meta = (attributes) => {
		const element = doc.createElement('meta')
		Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value))
		return element
	}
	doc.head.prepend(
		meta({ 'http-equiv': 'Content-Security-Policy', content: EMAIL_PREVIEW_POLICY }),
		meta({ charset: 'utf-8' }),
		meta({ name: 'referrer', content: 'no-referrer' })
	)
	return `<!doctype html>\n${doc.documentElement.outerHTML}`
}

// Only a draft definition can be edited; live/paused workflows are duplicated instead.
// Pre-editMode fallback: see cadenceEditMode for what a paused workflow may change.
export const isCadenceDefinitionImmutable = (status) => ['active', 'paused', 'archived'].includes(status)

export const STRUCTURE_LOCKED_HINT = 'Not available while candidates are mid-cadence.'
export const RETURN_TO_DRAFT_CONSEQUENCE = 'Not running. Back to draft so you can edit everything before publishing again.'

// What the builder may change, from the API's `editMode`:
//   FULL      draft, or paused with nobody mid-cadence — everything editable
//   SAFE      paused with candidates mid-cadence — templates, send timing, delays, names/settings only
//   READ_ONLY live or archived
// A draft is always FULL and live/archived always READ_ONLY. An API that predates
// `editMode` omits it, so a paused workflow keeps the old read-only behaviour.
export const cadenceEditMode = ({ status, editMode, canEdit = true } = {}) => {
	if (!canEdit) return 'READ_ONLY'
	const current = status || 'draft'
	if (current === 'draft') return 'FULL'
	if (current !== 'paused') return 'READ_ONLY'
	const mode = String(editMode || '').toUpperCase()
	return mode === 'FULL' || mode === 'SAFE' ? mode : 'READ_ONLY'
}

// readOnly: nothing is editable. structureLocked: steps, paths, branches, conditions,
// exit rule, audience and re-check are locked. lockReason: tooltip on those controls
// when they are shown disabled (SAFE); read-only hides them as before.
export const cadenceEditPermissions = (args) => {
	const mode = cadenceEditMode(args)
	return { mode, readOnly: mode === 'READ_ONLY', structureLocked: mode !== 'FULL', lockReason: mode === 'SAFE' ? STRUCTURE_LOCKED_HINT : null }
}

export const cadenceBuilderNotice = ({ status, mode }) => {
	if (!status || status === 'draft') return null
	if (status === 'archived') return 'This workflow is archived and can no longer run. Duplicate it to start a new draft.'
	if (status === 'paused' && mode === 'SAFE')
		return 'This workflow is paused with candidates mid-cadence. You can change templates, send timing and delays; adding or removing steps needs no active candidates.'
	if (status === 'paused' && mode === 'FULL') return 'This workflow is paused. Changes apply when you set it back to Live.'
	return `This workflow is ${cadenceStatusLabel(status).toLowerCase()}, so its steps are read-only. Duplicate it as a draft to make changes.`
}

// Rolling windows counted from today; the value is a whole number of days (smart comms S3 adds the last two).
export const DAY_OPERATORS = ['within_last_days', 'within_next_days', 'more_than_days_ago']
export const isDayOperator = (operator) => DAY_OPERATORS.includes(operator)

export const operatorLabel = (operator) =>
	({
		eq: 'is',
		neq: 'is not',
		not_eq: 'is not',
		gt: 'is greater than',
		gte: 'is at least',
		lt: 'is less than',
		lte: 'is at most',
		in: 'is one of',
		not_in: 'is not one of',
		between: 'is between',
		contains: 'contains',
		before: 'is before',
		after: 'is after',
		within_last_days: 'is within the last',
		within_next_days: 'is within the next',
		more_than_days_ago: 'is more than',
		is_set: 'is known',
		is_not_set: 'is unknown',
	}[operator] || String(operator || '').replace(/_/g, ' '))

// Plain-text row for a read-only rule, e.g. "Profile completion is greater than 0".
export const describeRuleCondition = (condition, attributes = []) => {
	const attribute = (attributes || []).find((item) => item?.key === condition?.attribute)
	const name = attribute?.label || sentenceCase(condition?.attribute) || 'Unknown field'
	const operator = operatorLabel(condition?.operator || 'eq')
	if (['is_set', 'is_not_set'].includes(condition?.operator)) return `${name} ${operator}`
	const format = (value) => (typeof value === 'boolean' ? (value ? 'True' : 'False') : String(value))
	const raw = condition?.value
	let value = '—'
	if (Array.isArray(raw)) value = raw.map(format).join(condition.operator === 'between' ? ' and ' : ', ')
	else if (raw !== undefined && raw !== null && raw !== '') value = format(raw)
	if (isDayOperator(condition?.operator)) {
		const days = `${value} ${Number(raw) === 1 ? 'day' : 'days'}`
		return `${name} ${operator} ${condition.operator === 'more_than_days_ago' ? `${days} ago` : days}`
	}
	return `${name} ${operator} ${value}`
}

export const allowedTransitions = {
	draft: ['active', 'archived'],
	active: ['paused'],
	paused: ['active', 'archived'],
	archived: [],
}

export const escapeHtml = (value) =>
	String(value || '').replace(/[&<>'"]/g, (character) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;' }[character]))

// JSON rules are validated by the API with strict types. Browser fields yield
// strings, so coerce only the registry-declared primitives before atomic save.
export const coerceRuleValue = (type, value, operator) => {
	if (value === '') return ''
	// A number of days, whatever the attribute's own type (a date).
	if (isDayOperator(operator)) return Number.parseInt(value, 10)
	const collection = ['between', 'in', 'not_in'].includes(operator)
	let values = [value]
	if (collection) {
		values = Array.isArray(value) ? value : String(value).split(',')
		values = values.map((item) => (typeof item === 'string' ? item.trim() : item))
	}
	const scalar = (item) => {
		if (type === 'integer') return Number.parseInt(item, 10)
		if (type === 'decimal') return Number.parseFloat(item)
		if (type === 'boolean') return item === true || item === 'true'
		return item
	}
	const cast = values.map(scalar)
	return collection ? cast : cast[0]
}

export const nextConditionForAttribute = (attribute) => ({
	attribute: attribute.key,
	operator: attribute.operators?.[0] || 'eq',
})

export const nextConditionForOperator = (condition, operator) => {
	const next = { ...condition, operator }
	if (['is_set', 'is_not_set'].includes(operator)) delete next.value
	else if (['between', 'in', 'not_in'].includes(operator) && !Array.isArray(next.value))
		next.value = next.value === undefined || next.value === '' ? [] : [next.value]
	else if (Array.isArray(next.value)) next.value = next.value[0] ?? ''
	return next
}
